// Per-route <title> and meta description copy.
//
// Kept as data rather than inline in each view so the whole site's SERP
// appearance can be reviewed in one place. Rules applied here:
//   - title <= 60 chars so it is not truncated in Google's desktop SERP
//   - description 120-155 chars, written as ad copy with a reason to click
//   - the primary keyword sits at the front of the title, the brand at the end
//   - every title is unique; duplicates are the single most common cause of
//     Google collapsing pages together

export const PAGE_META = {
  home: {
    title: "Paper Distributor in Delhi | Sigma Enterprises",
    description:
      "Wholesale paper & board distributor in New Delhi. Kraft, art card, SBS, duplex, copier & specialty paper at trade prices. Ready stock, pan-India delivery.",
  },
  catalogue: {
    title: "Paper & Board Catalogue | Sigma Enterprises Delhi",
    description:
      "Browse 20+ paper and board product lines — coated, uncoated, kraft, specialty, coloured and adhesive stock. Wholesale supply from New Delhi across India.",
  },
  about: {
    title: "About Sigma Enterprises | Paper Wholesaler, Delhi",
    description:
      "Sigma Enterprises is a New Delhi wholesale paper and board distributor supplying printers, packaging converters and traders across India with ready stock.",
  },
  contact: {
    title: "Contact Sigma Enterprises | Paper Supplier Delhi",
    description:
      "Call +91 99992 42566 or WhatsApp us for a trade quote on paper and board. Visit our Ramesh Nagar, New Delhi office — open Mon to Sat, 10am to 8pm.",
  },
  export: {
    title: "Paper & Board Export from India | Sigma Enterprises",
    description:
      "IEC-registered paper and board supplier in New Delhi exporting to Nepal, Bhutan, Bangladesh, Sri Lanka, the UAE and Europe. Export enquiries welcome.",
  },
  brands: {
    title: "Paper Brands We Stock in Delhi | Sigma Enterprises",
    description:
      "Paper and board brands stocked in New Delhi — JK Paper, PaperOne, Century, Trident, APP, APRIL, Cosmo and Avery Dennison. Call for current availability.",
  },
  faq: {
    title: "Paper & Board FAQs | Sigma Enterprises, Delhi",
    description:
      "How we quote, what we stock, how to choose a gsm, and exporting from India to Nepal, Bhutan, Bangladesh, Sri Lanka, the UAE and Europe. Questions answered.",
  },
  notFound: {
    title: "Page Not Found | Sigma Enterprises",
    description:
      "The page you are looking for does not exist. Browse the Sigma Enterprises paper and board catalogue or get in touch for a trade quote.",
  },
};

// Category-page overrides, keyed by the slug in src/sigma/data.js. Each one
// targets the commercial query a buyer in Delhi would actually type for that
// product family, not the internal category label.
//
// `h1` matters as much as `title`: the internal category names ("Kraft &
// Packaging Boards") describe the shelf, not the search. The H1 names the
// product and the city, which is what the query does.
export const CATEGORY_META = {
  "coated-papers-boards": {
    h1: "Coated Paper & Board — Wholesale in Delhi",
    title: "Coated Paper & Board Supplier in Delhi | Sigma",
    description:
      "Art card 210-350 gsm, art paper 80-170 gsm and SBS board 190-350 gsm from stock in New Delhi. Wholesale coated paper and board for print and packaging.",
    intro:
      "Coated paper and board stocked in New Delhi for printers and packaging converters — art card, art paper and SBS board in the grammages the trade orders most, available in wholesale quantities with pan-India delivery.",
  },
  "uncoated-printing": {
    h1: "Copier, Printing & Textured Paper — Wholesale in Delhi",
    title: "Copier & Printing Paper Wholesaler in Delhi | Sigma",
    description:
      "Wholesale copier paper 70-100 gsm, offset printing paper, super-white maplitho and 50+ textured papers. Bulk supply from our New Delhi warehouse.",
    intro:
      "Everyday printing and writing stock in wholesale quantities — copier reams, offset printing paper, high-white maplitho and a textured range running from 120 gsm paper to 300 gsm board.",
  },
  "kraft-packaging-boards": {
    h1: "Packaging Board — Kraft, Duplex & Kappa, Wholesale in Delhi",
    title: "Packaging Board Supplier in Delhi | Kraft, Duplex",
    description:
      "High-burst kraft paper and board, duplex board (grey & white back) and rigid kappa board from a New Delhi wholesaler. Trade pricing, ready stock, bulk supply.",
    intro:
      "Packaging-grade board from stock in New Delhi — high-burst kraft paper and kraft board for cartons and wrapping, economical duplex board for folding cartons, and dense kappa board for rigid boxes and binding.",
  },
  "specialty-papers": {
    h1: "Synthetic, Tracing & Non-Tearable Paper in Delhi",
    title: "Synthetic & Tracing Paper Supplier in Delhi | Sigma",
    description:
      "Waterproof synthetic paper, non-tearable paper, translucent tracing paper and fine tissue paper. Specialty stock supplied wholesale from New Delhi.",
    intro:
      "Engineered papers for technical and durable work — waterproof synthetic stock for outdoor labels, tear-proof paper for tags and maps, translucent tracing paper for design work, and lightweight tissue for wrapping and interleaving.",
  },
  "coloured-specialty-stock": {
    h1: "Colour & Black Paper — Wholesale Supply in Delhi",
    title: "Colour & Black Paper Supplier in Delhi | Sigma",
    description:
      "Vivid coloured paper and deep matt black paper for premium packaging, mounts and craft work. Wholesale supply from Sigma Enterprises, New Delhi.",
    intro:
      "Coloured and black stock for creative and premium work — assorted colour paper for craft and print, and dense deep-matt black paper for premium packaging and mounts.",
  },
  "adhesive-paper": {
    h1: "Self-Adhesive Paper & Label Stock — Wholesale in Delhi",
    title: "Self-Adhesive Paper & Label Stock Supplier, Delhi",
    description:
      "Chromo, PVC, woodfree, vinyl and kraft self-adhesive face stock for labels and stickers. Wholesale label paper supply from New Delhi, pan-India delivery.",
    intro:
      "Self-adhesive face stock for label and sticker printing — gloss-coated chromo, waterproof PVC, writable woodfree, weatherproof vinyl and natural brown kraft, all supplied on the roll or in sheets to trade requirements.",
  },
};

export function categoryMeta(cat) {
  const override = CATEGORY_META[cat.slug];
  return {
    h1: cat.name,
    title: `${cat.name} Supplier in Delhi | Sigma Enterprises`,
    description: cat.blurb,
    intro: cat.blurb,
    ...override,
  };
}
